import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { Stopwatch } from './stopwatch'

/** The colour of each tone (spec §26). */
export const TONE_CLASS = {
	ready: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300',
	attention: 'border-amber-500/60 bg-amber-500/10 text-amber-800 dark:text-amber-300',
	refused: 'border-red-500/60 bg-red-500/10 text-red-800 dark:text-red-300',
} as const

export type Tone = keyof typeof TONE_CLASS

const TONE_ICON = { ready: CheckCircle2, attention: AlertTriangle, refused: XCircle } as const

/**
 * The answer to the last scan or act, in one of three tones (spec §26):
 * «готово» green, «внимание» yellow, «отказ» red. The tone's icon and word on top, the
 * headline large enough to read from the bench, the detail under it, and the acts on
 * what was just answered («Повреждена») at its top right. Every scanning page — the hub's
 * and the Point's — answers with this one block, at the same size, so the worker reads
 * it the same way everywhere.
 * The colour is the server's. A refusal is announced at once (`role="alert"`); the
 * rest politely.
 */
export function ToneBanner({
	tone,
	title,
	detail,
	testId,
	titleTestId,
	detailTestId,
	attributes,
	actions,
	since,
	children,
}: {
	tone: Tone
	/** The headline — where the thing goes, or what happened. */
	title: ReactNode
	/** The line under it: the code, and why. */
	detail?: ReactNode
	testId: string
	titleTestId?: string
	detailTestId?: string
	/** Extra `data-*` attributes on the banner — its outcome, its reason. */
	attributes?: Record<string, string | undefined>
	/** Acts on what was just answered, at the banner's top right — «Повреждена». */
	actions?: ReactNode
	/** When the answer came (epoch ms): a stopwatch beside the tone counts the time since. */
	since?: number
	/** Further lines (what a Box was short of, for one). */
	children?: ReactNode
}) {
	const { t } = useTranslate()
	const Icon = TONE_ICON[tone]
	return (
		<div
			role={tone === 'refused' ? 'alert' : 'status'}
			className={cn('flex items-start gap-4 rounded-lg border px-5 py-4', TONE_CLASS[tone])}
			data-testid={testId}
			data-tone={tone}
			{...attributes}
		>
			<div className='flex min-w-0 flex-1 flex-col gap-1.5'>
				<span className='flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase'>
					<Icon className='size-4' aria-hidden />
					{t(`hubScanTone.${tone}`)}
					{since !== undefined && (
						<span className='ml-auto pl-3 font-medium normal-case opacity-80'>
							<Stopwatch key={since} since={since} testId={`${testId}-stopwatch`} />
						</span>
					)}
				</span>
				<div
					className='text-4xl leading-tight font-extrabold tracking-tight text-balance break-words'
					data-testid={titleTestId}
				>
					{title}
				</div>
				{detail !== undefined && (
					<div className='text-base font-medium' data-testid={detailTestId}>
						{detail}
					</div>
				)}
				{children}
			</div>
			{actions && <div className='flex shrink-0 items-center gap-2 pt-1'>{actions}</div>}
		</div>
	)
}

/**
 * The outcome of an act that is not a scan — «Рейс закрыт», «Сумка упакована», «Смена
 * закрыта с недостачей» — in the same three tones, quieter than `ToneBanner`: a short
 * title, a line under it, and, if the outcome leads somewhere, its acts at the right.
 * It stands where the act was taken, above what the act changed.
 */
export function ToneNote({
	tone,
	title,
	detail,
	testId,
	actions,
}: {
	tone: Tone
	title: ReactNode
	detail?: ReactNode
	testId?: string
	actions?: ReactNode
}) {
	return (
		<div
			role={tone === 'refused' ? 'alert' : 'status'}
			className={cn('flex items-start gap-4 rounded-lg border px-4 py-3', TONE_CLASS[tone])}
			data-testid={testId}
			data-tone={tone}
		>
			<div className='flex min-w-0 flex-1 flex-col gap-0.5'>
				<div className='font-semibold'>{title}</div>
				{detail !== undefined && <div className='text-sm'>{detail}</div>}
			</div>
			{actions && <div className='flex shrink-0 items-center gap-2'>{actions}</div>}
		</div>
	)
}
